// GENERATED from vec-core-backend/bin/hpBalance.js - edit that file and run `node bin/syncAdminUtils.js` (a backend test fails when they differ).
/**
 * Hire purchase balance - one definition of "what is still owed" for the payment cap, the agreement screen, closing,
 * the scheduler's percentage penalties, prints and reports. Integer cents throughout, so a balance of exactly 0 is exactly 0
 * (the old float maths left 1e-12 residue that made `balance === 0` false).
 *
 *   owed = agreed amount + active penalties - active discounts - receipts
 *
 * "active" = the entry has not been deleted (deletion adds a deletedAt property to the entry).
 * A copy of this file lives in vec-core-admin (src/utils/hpBalance.js); a backend test fails when they differ.
 */
import { toCents, fromCents } from './orderTotals';

const isActive = (e) => !!e && !Object.prototype.hasOwnProperty.call(e, 'deletedAt') && !e.deletedAt;
const sumActive = (list) => (Array.isArray(list) ? list : []).filter(isActive).reduce((a, e) => a + toCents(e.amount || 0), 0);

/** the agreed amount: the latest value of the agreement's amount history, else the order's grand total */
function agreedAmount(hpa, orderGrandTotal) {
    const hist = hpa && Array.isArray(hpa.amount) ? hpa.amount : [];
    const last = hist.length ? hist[hist.length - 1] : null;
    if (last && last.value !== undefined && last.value !== null && last.value !== '') return fromCents(toCents(last.value));
    return fromCents(toCents(orderGrandTotal || 0));
}

/**
 * @param {object} p
 * @param {number} p.agreed
 * @param {Array}  p.penalties   [{amount, deletedAt?}]
 * @param {Array}  p.discounts   [{amount, deletedAt?}]
 * @param {number} p.paid        sum of the agreement's non voided receipts
 * @returns {{owed:number, penalties:number, discounts:number, agreed:number, paid:number}}
 */
function hpBalance(p) {
    const agreed = toCents(p.agreed || 0);
    const pen = sumActive(p.penalties);
    const disc = sumActive(p.discounts);
    const paid = toCents(p.paid || 0);
    return { agreed: fromCents(agreed), penalties: fromCents(pen), discounts: fromCents(disc), paid: fromCents(paid), owed: fromCents(agreed + pen - disc - paid) };
}

/**
 * Payment cap with change: the amount that is applied to the agreement is never more than what is owed; whatever was
 * tendered beyond it is change for the customer (or, for a credit note, a new credit note for the difference).
 * @returns {{applied:number, change:number, owed:number}}
 */
function capPayment(tendered, owed) {
    const t = toCents(tendered);
    const o = Math.max(0, toCents(owed));
    const applied = Math.min(t, o);
    return { applied: fromCents(applied), change: fromCents(t - applied), owed: fromCents(o) };
}

/** a percentage penalty, rounded to cents and taken on the owed balance (never on a negative balance) */
function percentPenalty(owed, percent) {
    const base = Math.max(0, toCents(owed));
    return fromCents(toCents(fromCents(base) * ((parseFloat(percent) || 0) / 100)));
}

export { isActive, agreedAmount, hpBalance, capPayment, percentPenalty };
